// UserBbsInfoInput.js
import React, { useEffect, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Paper } from '@mui/material';
import Draggable from 'react-draggable';
import { AppStyle, AppWord, AppObject, CodeGroup } from '../../../value/AppConst';
import { UserBbsInfoDict, makeUserBbsCatOptions } from './UserBbsInfoDict';
import { useConfirmDialog } from '../../../common/AppConfirmDialog';
import VzmTextField from '../../../common/VzmTextField';
import VzmTextareaField from '../../../common/VzmTextareaField';
import VzmSelector from '../../../common/VzmSelector';
import { VzmInputBlock } from '../../../common/VzmStyled';
import VzmCheckbox from '../../../common/VzmCheckbox';
import InputUtil from '../../../value/InputUtil';
import ValueMisc from '../../../value/ValueMisc';
import { useReactiveVar } from '@apollo/client';
import { userInfoRepo, codeTreeRepo } from '../../../value/AppModel';
import UserBbsFiles from './UserBbsFiles';

const EditorTitle = styled(DialogTitle)({
    backgroundColor: AppStyle.HeaderBackgroundColor,
    color: 'white', minWidth: 300, cursor:'move'
});
const NameBox = styled(Box)({
    fontSize: '0.8rem', padding: 5, textAlign: 'right'
});

const InputRow = styled(Box)({
    display: 'flex', alignItems: 'center', minWidth: 600, marginTop:10
});
const InputColumnBlock = styled(Box)({flexGrow:1,});

const InputMap = UserBbsInfoDict.inputMap;
const defaultInputData = InputUtil.defaultValuesFromInputMap(InputMap);

const DraggableTitleId = 'draggable-dialog-title-user-bbs';
function PaperComponent(props) {
    return (
        <Draggable
            handle={'#'+DraggableTitleId}
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} />
        </Draggable>
    );
}

/**
 * 사용자 게시판 입력 화면
 */
export default function UserBbsInfoInput({
    open,
    item,
    onClose,
    onRequestSave,
    onRequestReload,
    responseSaving, // Response if result is NOT OK.
}) {
    const [input, setInput] = useState({...AppObject.emptyInputData});
    const [isEdit, setIsEdit] = useState(false); // true이면 파일 업로드, 삭제 가능함.
    const openConfirmDialog = useConfirmDialog();
    const sessionInfo = useReactiveVar(userInfoRepo);
    const codeTree = useReactiveVar(codeTreeRepo);
    const {loading:loadingSave, error:errorSave, data:resultSave} = responseSaving; // 전송 결과에 대한 View 기능 소화.

    useEffect(()=>{
        if(open) {
            const inputSet = {...AppObject.emptyInputData};
            let editing = false;
            if(item) {
                inputSet.data = {...item};
                editing = true;
            }
            else {
                inputSet.data = {...defaultInputData};
            }
            setInput(inputSet);
            setIsEdit(editing);
        }
    }, [open, item]);

    const onChangeValue = (id, value, error) => {
        const newInput = InputUtil.reflectData(input, id, value, error, InputMap, isEdit);
        setInput(newInput);
    };

    const onClickSubmit = () => {
        const param = InputUtil.getDataToSubmit(input.data, InputMap, isEdit);
        onRequestSave(param, isEdit);
    };

    const askCancel = async () => {
        const confirm = await openConfirmDialog({
            ...AppObject.messageCancelEdit,
            option: {yesLabel: AppWord.IgnoreAndClose}
        });
        if(confirm) onClose();
    };

    const onClickCancel = () => {
        if(input.changed) askCancel();
        else onClose();
    };

    if(!open) return null;

    return(
        <Dialog open={open} maxWidth='lg'
            PaperComponent={PaperComponent}
            aria-labelledby={DraggableTitleId}
        >
            <EditorTitle id={DraggableTitleId}>
                {
                    isEdit ? '게시물 수정' : '게시물 등록'
                }
                <NameBox>
                    {sessionInfo.user.userName}
                </NameBox>
            </EditorTitle>
            <DialogContent>
                <VzmInputBlock>
                    <VzmTextField
                        dict={InputMap.title}
                        value={input.data.title}
                        onChange={onChangeValue}
                        fullWidth={true}
                    />
                </VzmInputBlock>
                <VzmInputBlock>
                    <VzmTextField
                        dict={InputMap.email}
                        value={input.data.email}
                        onChange={onChangeValue}
                        fullWidth={true}
                    />
                </VzmInputBlock>
                <VzmInputBlock>
                    <VzmTextareaField
                        dict={InputMap.content}
                        value={input.data.content}
                        onChange={onChangeValue}
                        fullWidth={true}
                    />
                </VzmInputBlock>
                <InputRow>
                    <InputColumnBlock>
                    <VzmCheckbox
                        dict={InputMap.topOrder}
                        value={input.data.topOrder}
                        onChange={onChangeValue}
                    />
                    </InputColumnBlock>
                    <InputColumnBlock>
                    <VzmSelector
                        dict={InputMap.userBbsCatCd}
                        value={input.data.userBbsCatCd}
                        list={makeUserBbsCatOptions(ValueMisc.getCodes(codeTree, CodeGroup.USER_BBS_CAT_CD))}
                        onChange={onChangeValue}
                        fullWidth={true}
                    />
                    </InputColumnBlock>
                </InputRow>
                {
                    isEdit &&
                    <UserBbsFiles
                        item={item}
                        actionDisabled={input.changed}
                        onRequestReload={onRequestReload}
                    />}
            </DialogContent>
            <DialogActions>
                {
                    loadingSave
                    ?
                    <span>
                        <CircularProgress />
                        &nbsp;데이터 저장 중...
                    </span>
                    :
                    <>
                        <Button
                            onClick={onClickSubmit}
                            disabled={input.hasError || !input.changed}
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.primary}
                        >저장</Button>
                        <Button onClick={onClickCancel}
                            color={AppStyle.mui.warning}
                            variant={AppStyle.mui.contained}
                        >취소</Button>
                    </>
                }
            </DialogActions>
        </Dialog>
    );
}